import React, { useState, useCallback, useEffect } from 'react';
import { Alert, AlertTitle, Box, CircularProgress, Snackbar, Stack } from '@mui/material';

import api from '../services/api';
import { waitForJob as waitForJobShared } from '../utils/waitForJob';
import { useData } from '../context/DataContext';
import {
  CATEGORY_COLORS,
  DEFAULT_CATEGORY_COLORS,
  saveCustomColors,
  resetCategoryColors,
} from '../utils/colors';

import PageHeader from '../components/ui/PageHeader';
import JiraSection from '../components/admin/JiraSection';
import CategoryColorsSection from '../components/admin/CategoryColorsSection';
import SampleDataSection from '../components/admin/SampleDataSection';
import CategoryManagementSection from '../components/admin/CategoryManagementSection';
import DiagnosticsSection from '../components/admin/DiagnosticsSection';
import EffortManagementSection from '../components/admin/EffortManagementSection';
import JevEstimationSection from '../components/admin/JevEstimationSection';
import EstimationTesterSection from '../components/admin/EstimationTesterSection';
import useEffortManagement from '../components/admin/useEffortManagement';
import useJevEstimation from '../components/admin/useJevEstimation';

/** Maps common Jira connection failures to actionable remediation advice. */
const connectionAdvice = (message, status) => {
  if (message.includes('404') || status === 404) {
    return ' The API endpoint /api/jira/test could not be found. Check that your server is running and routes are properly configured.';
  }
  if (message.includes('JIRA_BASE_URL') || message.includes('URL')) {
    return ' Ensure JIRA_BASE_URL environment variable is set correctly (e.g., https://your-domain.atlassian.net).';
  }
  if (message.includes('JIRA_API_EMAIL') || message.includes('Email')) {
    return ' Ensure JIRA_API_EMAIL environment variable is set correctly.';
  }
  if (
    message.includes('JIRA_API_TOKEN') ||
    message.includes('Token') ||
    message.includes('Authentication failed') ||
    message.includes('401')
  ) {
    return ' Ensure JIRA_API_TOKEN environment variable is set correctly. You may need to generate a new API token in your Atlassian account.';
  }
  if (message.includes('Network Error') || message.includes('ECONNREFUSED')) {
    return ' Check your server can reach the Jira instance and that the URL is correct.';
  }
  return '';
};

const Admin = () => {
  const { fetchTickets, fetchAnalytics } = useData();

  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'info' });
  const notify = useCallback((message, severity = 'info') => {
    setFeedback({ open: true, message, severity });
  }, []);

  // Jira connection / sync
  const [syncLoading, setSyncLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [jiraConfigStatus, setJiraConfigStatus] = useState(null);
  const [jiraConfigLoading, setJiraConfigLoading] = useState(false);
  const [jiraConfigExpanded, setJiraConfigExpanded] = useState(false);

  // Database health
  const [databaseStatus, setDatabaseStatus] = useState({ available: true, status: 'unknown' });
  const [statusLoading, setStatusLoading] = useState(true);

  // Sample data
  const [sampleDataLoading, setSampleDataLoading] = useState(false);
  const [clearDataLoading, setClearDataLoading] = useState(false);
  const [sampleDataOptions, setSampleDataOptions] = useState({ count: 50, dataType: 'mixed' });

  // Category colours + bulk categorization
  const [categoryColors, setCategoryColors] = useState({ ...CATEGORY_COLORS });
  const [bulkCategoryOptions, setBulkCategoryOptions] = useState({
    category: '',
    filter: 'uncategorized',
    keyword: '',
  });
  const [bulkCategoryLoading, setBulkCategoryLoading] = useState(false);

  // Diagnostics
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsResults, setDiagnosticsResults] = useState(null);
  const [fixCategoriesLoading, setFixCategoriesLoading] = useState(false);
  const [fixResults, setFixResults] = useState(null);

  const effort = useEffortManagement(notify);
  const jev = useJevEstimation(notify, fetchTickets);

  useEffect(() => {
    const checkDatabaseStatus = async () => {
      try {
        setStatusLoading(true);
        const response = await api.get('/health');
        setDatabaseStatus({
          available: response.data.database.status === 'connected',
          status: response.data.database.status,
        });
      } catch (error) {
        console.error('Error checking database status:', error);
        setDatabaseStatus({ available: false, status: 'error' });
      } finally {
        setStatusLoading(false);
      }
    };
    checkDatabaseStatus();
  }, []);

  const fetchJiraConfigStatus = useCallback(async () => {
    try {
      setJiraConfigLoading(true);
      const response = await api.get('/jira/config-status');
      setJiraConfigStatus(response.data.configStatus);
    } catch (error) {
      console.error('Error fetching Jira config status:', error);
      notify('Failed to fetch Jira configuration status', 'error');
    } finally {
      setJiraConfigLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (jiraConfigExpanded && !jiraConfigStatus) fetchJiraConfigStatus();
  }, [jiraConfigExpanded, jiraConfigStatus, fetchJiraConfigStatus]);

  /**
   * Poll a background job to completion.
   *
   * The sync and clear endpoints now return 202 + a jobId instead of holding
   * the request open, because anything past ~30s was being killed by
   * CloudFront and surfaced to the user as "status code 504".
   */
  const waitForJob = useCallback((jobId, options = {}) => waitForJobShared(api, jobId, options), []);

  const handleSyncClick = useCallback(async () => {
    setSyncLoading(true);
    try {
      const started = await api.post('/jira/sync');
      notify(started.data.message || 'Jira sync started.', 'info');

      const job = await waitForJob(started.data.jobId);
      notify(job.result?.message || 'Jira sync complete.', 'success');

      try {
        await fetchTickets();
        await fetchAnalytics();
      } catch (refreshError) {
        console.error('Error refreshing frontend data after sync:', refreshError);
      }
    } catch (error) {
      console.error('Error triggering Jira sync:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to trigger Jira sync.';
      notify(`Sync failed: ${errorMessage}`, 'error');
    } finally {
      setSyncLoading(false);
    }
  }, [fetchTickets, fetchAnalytics, notify, waitForJob]);

  const handleTestConnectionClick = useCallback(async () => {
    setTestLoading(true);
    try {
      const response = await api.get('/jira/test');
      notify(
        response.data.message || 'Connection test completed.',
        response.data.success ? 'success' : 'error'
      );
    } catch (error) {
      console.error('Error testing Jira connection:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Unknown error occurred while testing the connection';
      notify(errorMessage + connectionAdvice(errorMessage, error.status), 'error');
    } finally {
      setTestLoading(false);
    }
  }, [notify]);

  const handleGenerateSampleData = useCallback(async () => {
    setSampleDataLoading(true);
    try {
      const response = await api.post('/sample-data/generate', sampleDataOptions);
      notify(`Successfully generated ${response.data.count} sample tickets`, 'success');
      await fetchTickets();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error generating sample data:', error);
      notify(`Failed to generate sample data: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setSampleDataLoading(false);
    }
  }, [sampleDataOptions, fetchTickets, fetchAnalytics, notify]);

  const handleClearData = useCallback(async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete all ticket data? This action cannot be undone.'
      )
    ) {
      return;
    }
    setClearDataLoading(true);
    try {
      const started = await api.delete('/tickets');
      notify(started.data.message || 'Clearing ticket data…', 'info');

      const job = await waitForJob(started.data.jobId);
      notify(`Deleted ${job.result?.deletedCount ?? 0} tickets`, 'success');

      await fetchTickets();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error clearing data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      notify(`Failed to clear data: ${errorMessage}`, 'error');
    } finally {
      setClearDataLoading(false);
    }
  }, [fetchTickets, fetchAnalytics, notify, waitForJob]);

  const handleBulkCategoryUpdate = useCallback(async () => {
    setBulkCategoryLoading(true);
    try {
      const response = await api.post('/tickets/bulk-category', bulkCategoryOptions);
      notify(`Successfully updated ${response.data.count} tickets`, 'success');
      await fetchTickets();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error updating tickets:', error);
      notify(`Failed to update tickets: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setBulkCategoryLoading(false);
    }
  }, [bulkCategoryOptions, fetchTickets, fetchAnalytics, notify]);

  const handleRunDiagnostics = useCallback(async () => {
    setDiagnosticsLoading(true);
    try {
      const response = await api.get('/diagnostic/tickets');
      setDiagnosticsResults(response.data);
      notify(
        `Diagnostics complete: Found ${response.data.ticketCount} tickets, ${response.data.uncategorizedCount} uncategorized`,
        'success'
      );
      await fetchTickets();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error running diagnostics:', error);
      notify(`Failed to run diagnostics: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setDiagnosticsLoading(false);
    }
  }, [fetchTickets, fetchAnalytics, notify]);

  const handleFixCategories = useCallback(async () => {
    setFixCategoriesLoading(true);
    try {
      const response = await api.post('/fix/categories');
      setFixResults(response.data);
      notify(
        `Fixed ${response.data.uncategorizedFixed} uncategorized tickets and added missing labels to ${response.data.labelsMissing} tickets`,
        'success'
      );
      await fetchTickets();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error fixing categories:', error);
      notify(`Failed to fix categories: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setFixCategoriesLoading(false);
    }
  }, [fetchTickets, fetchAnalytics, notify]);

  const handleSaveColors = () =>
    saveCustomColors(categoryColors)
      ? notify('Category colors saved successfully', 'success')
      : notify('Failed to save category colors', 'error');

  const handleResetColors = () => {
    if (resetCategoryColors()) {
      setCategoryColors({ ...DEFAULT_CATEGORY_COLORS });
      notify('Category colors reset to defaults', 'success');
    } else {
      notify('Failed to reset category colors', 'error');
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <PageHeader
        title="Admin Control Panel"
        subtitle="Connection, data and estimation controls for the Squid dataset."
      />

      {statusLoading && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3 }}>
          Checking database connectivity…
        </Alert>
      )}

      {!statusLoading && !databaseStatus.available && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Database connection unavailable</AlertTitle>
          The database is currently {databaseStatus.status}. Authentication and Jira connectivity
          tests still work, but ticket, analytics and sample-data features are disabled until the
          connection is restored.
        </Alert>
      )}

      <Stack spacing={3}>
        <JiraSection
          testLoading={testLoading}
          syncLoading={syncLoading}
          onTestConnection={handleTestConnectionClick}
          onSync={handleSyncClick}
          databaseAvailable={databaseStatus.available}
          jiraConfigStatus={jiraConfigStatus}
          jiraConfigLoading={jiraConfigLoading}
          jiraConfigExpanded={jiraConfigExpanded}
          setJiraConfigExpanded={setJiraConfigExpanded}
          fetchJiraConfigStatus={fetchJiraConfigStatus}
        />

        <CategoryColorsSection
          colors={categoryColors}
          onColorChange={(category, color) =>
            setCategoryColors((prev) => ({ ...prev, [category]: color }))
          }
          onSave={handleSaveColors}
          onReset={handleResetColors}
        />

        <SampleDataSection
          options={sampleDataOptions}
          onOptionChange={(event) =>
            setSampleDataOptions((prev) => ({ ...prev, [event.target.name]: event.target.value }))
          }
          onSliderChange={(name) => (event, newValue) =>
            setSampleDataOptions((prev) => ({ ...prev, [name]: newValue }))}
          onGenerate={handleGenerateSampleData}
          onClear={handleClearData}
          sampleDataLoading={sampleDataLoading}
          clearDataLoading={clearDataLoading}
          databaseAvailable={databaseStatus.available}
        />

        <CategoryManagementSection
          options={bulkCategoryOptions}
          onChange={(event) =>
            setBulkCategoryOptions((prev) => ({ ...prev, [event.target.name]: event.target.value }))
          }
          onApply={handleBulkCategoryUpdate}
          loading={bulkCategoryLoading}
        />

        <DiagnosticsSection
          onRunDiagnostics={handleRunDiagnostics}
          onFixCategories={handleFixCategories}
          diagnosticsLoading={diagnosticsLoading}
          fixCategoriesLoading={fixCategoriesLoading}
          diagnosticsResults={diagnosticsResults}
          fixResults={fixResults}
        />

        <EffortManagementSection {...effort} />

        <JevEstimationSection {...jev.run} />

        <EstimationTesterSection {...jev.tester} />
      </Stack>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={feedback.severity} variant="filled">
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(Admin);
